import React, { useMemo } from 'react';
import { Clock, Users, TrendingUp, Download, DollarSign } from 'lucide-react';

/**
 * BusinessImpact - Shareholder-ready metrics
 * Balanced: Clean card, colorful icons and values (solid colors)
 */
const BusinessImpact = ({ analytics, hourlyRate = 30 }) => {
    const metrics = useMemo(() => {
        const conversations = analytics?.messageVolume?.totalConversations || 0;
        const resolvedTickets = analytics?.ticketMetrics?.resolvedTickets || 0;
        const openTickets = analytics?.ticketMetrics?.openTickets || 0;
        const totalTickets = openTickets + resolvedTickets;

        const minutesSaved = resolvedTickets * 5;
        const hoursSaved = Math.round(minutesSaved / 60);
        const costSaved = hoursSaved * hourlyRate;
        const containmentRate = totalTickets > 0 ? Math.round((resolvedTickets / totalTickets) * 100) : 0;
        const leadsCapture = Math.round(conversations * 0.15);

        return { hoursSaved, costSaved, containmentRate, leadsCapture, conversations };
    }, [analytics, hourlyRate]);

    const impactCards = [
        { icon: Clock, label: 'Hours Saved', value: metrics.hoursSaved, suffix: 'hrs', subtext: `${metrics.conversations} handled`, color: '#3b82f6' },
        { icon: DollarSign, label: 'Est. Savings', value: metrics.costSaved, prefix: '$', subtext: `at $${hourlyRate}/hr`, color: '#10b981' },
        { icon: Users, label: 'Self-Service', value: metrics.containmentRate, suffix: '%', subtext: 'without human', color: '#8b5cf6' },
        { icon: TrendingUp, label: 'Leads', value: metrics.leadsCapture, subtext: 'emails collected', color: '#f59e0b' }
    ];

    return (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Business Impact</h3>
                    <p className="text-[10px] text-slate-400 mt-0.5">ROI metrics</p>
                </div>
                <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors">
                    <Download size={14} />
                </button>
            </div>

            {/* Metrics Grid - White background, colorful icons and values */}
            <div className="grid grid-cols-2 gap-4">
                {impactCards.map((card, index) => {
                    const Icon = card.icon;
                    return (
                        <div key={index} className="p-3 rounded-xl border border-slate-100">
                            <div className="flex items-center gap-2 mb-2">
                                <Icon size={14} style={{ color: card.color }} />
                                <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                                    {card.label}
                                </span>
                            </div>
                            <div className="text-xl font-bold text-slate-800">
                                {card.prefix}<span style={{ color: card.color }}>{card.value.toLocaleString()}</span>{card.suffix}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-0.5">{card.subtext}</p>
                        </div>
                    );
                })}
            </div>

            {/* Export CTA */}
            <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                <p className="text-xs text-slate-500">Generate investor report</p>
                <button className="px-3 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-lg hover:bg-slate-800 transition-colors">
                    Export PDF
                </button>
            </div>
        </div>
    );
};

export default BusinessImpact;
